import React, { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, Sparkles, Loader2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';

const DocumentSearch = ({ onResultClick }) => {
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [searchType, setSearchType] = useState('semantic');

  const handleSearch = async () => {
    if (!query.trim()) {
      toast({
        title: 'Enter search query',
        description: 'Please type something to search',
        variant: 'default'
      });
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/work/documents/search?query=${encodeURIComponent(
          query
        )}&semantic=${searchType === 'semantic'}`,
        {
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setResults(data.results || []);

      if (data.results.length === 0) {
        toast({
          title: 'No Results',
          description: 'No documents match your search',
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: 'Search Failed',
        description: 'Unable to search documents. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search documents... (e.g., 'budget discussions', 'meeting about timeline')"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10"
          />
        </div>
        <Button
          onClick={handleSearch}
          disabled={searching}
          className="bg-teal-600 hover:bg-teal-700"
        >
          {searching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Search Type Toggle */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-600">Search mode:</span>
        <Button
          variant={searchType === 'semantic' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSearchType('semantic')}
          className={searchType === 'semantic' ? 'bg-teal-600' : ''}
        >
          <Sparkles className="h-3 w-3 mr-1" />
          AI Semantic
        </Button>
        <Button
          variant={searchType === 'keyword' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSearchType('keyword')}
        >
          Keyword
        </Button>
      </div>

      {searchType === 'semantic' && (
        <p className="text-xs text-teal-700 bg-teal-50 p-2 rounded border border-teal-200">
          💡 AI Semantic Search understands meaning, not just keywords. Try "project delays" to find
          documents about timeline issues.
        </p>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">
            Found {results.length} document{results.length > 1 ? 's' : ''}
          </h3>
          {results.map((result) => (
            <div
              key={result.id}
              onClick={() => onResultClick && onResultClick(result.id)}
              className="p-3 border-2 border-gray-200 rounded-lg hover:border-teal-300 hover:bg-teal-50 cursor-pointer transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-gray-900">{result.title}</h4>
                <Badge variant="outline" className="text-xs">
                  {result.template}
                </Badge>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2 mb-2">{result.snippet}</p>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>📅 {new Date(result.created_at).toLocaleDateString()}</span>
                {result.occurrences > 0 && (
                  <span>• {result.occurrences} match{result.occurrences > 1 ? 'es' : ''}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentSearch;
